import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Incident } from '@models/incident.model';
import { formatActivityTime } from '@utils/formatDate';

interface IncidentActionItemProps {
  incident: Incident;
  onView: (incidentId: string) => void;
}

/**
 * Sprint 16, story-076. One incident row in the "Needs your action" block, sourced from
 * `usePendingIncidents` — the same hook that already produces the sidebar's Incidents badge.
 * Link-only: an incident's Acknowledge action lives on the incident feed card, not this summary
 * row, to keep this block a fast triage list.
 */
export const IncidentActionItem: React.FC<IncidentActionItemProps> = ({ incident, onView }) => {
  const projectName = incident.project?.name || 'N/A';

  const handleClick = () => onView(incident.incidentId);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      data-testid={`needs-your-action-incident-${incident.incidentId}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 text-sm truncate">{incident.name}</div>
        <div className="text-xs text-gray-600 truncate">
          {projectName}
          {incident.incidentTime && ` · Reported ${formatActivityTime(incident.incidentTime)}`}
        </div>
      </div>
      <ChevronRightIcon fontSize="small" className="text-gray-400 flex-shrink-0" />
    </div>
  );
};

export default IncidentActionItem;
